/**
 * CHECKLIST — методы режима чек-листа бронирований.
 *
 * Phase 5 modularization (Sprint 6, 2026-05-07).
 * Извлечено из public/js/app.js (DOMAIN: LIFECYCLE & MODE — секция чеклиста).
 *
 * Группировка/фильтрация:
 *   - groupByDay(services)               — группировка по day для отображения в чеклисте
 *   - groupBySupplier(services)          — группировка по contractor (поставщику)
 *   - findServiceInState(id, section)    — найти оригинальный service в state.X[]
 *   - passesChecklistFilter(service)     — фильтр по статусу (booked/paid/all)
 *
 * Render:
 *   - updateChecklistCounters()          — счётчики в шапке чек-листа
 *   - renderChecklist()                  — главный рендер контейнера
 *   - renderChecklistGroup(group)        — одна группа (по day или supplier)
 *   - renderChecklistServiceCard(service)— карточка одной услуги в чеклисте (~280 строк)
 *
 * Pure-helpers groupByDay / groupBySupplier также есть в public/js/utils/format-utils.js
 * (для backend / non-class использования).
 *
 * @module app/checklist
 */

const _t = (k, fb) => {
    const v = window.i18n?.t?.(k);
    return (v && v !== k) ? v : (fb || k);
};

/**
 * Прикрепляет методы checklist-mode к prototype класса.
 * @param {Function} ClassRef - класс ProfessionalQuoteCalculator
 */
export function attachChecklist(ClassRef) {
    Object.assign(ClassRef.prototype, {
        groupByDay(services) {
            const groups = {};
            services.forEach(service => {
                const day = service.day || 0;
                if (!groups[day]) groups[day] = [];
                groups[day].push(service);
            });

            return Object.keys(groups)
                .map(Number)
                .sort((a, b) => a - b)
                .map(day => ({
                    day,
                    services: groups[day],
                    total: groups[day].reduce((sum, s) => sum + (s.price * s.quantity), 0)
                }));
        },

        // Sprint 19h: модалка запроса поставщику из чеклиста (для одной group)
        openChecklistSupplierRequest(group) {
            const supplierName = group.supplier || '';
            const services = group.services || [];
            const tourName = this.state.tourName || this.state.clientName || _t('app.checklist.default_tour', 'tour');

            const items = services.map(svc => ({
                service_name: svc.name,
                service_description: svc.description,
                service_type: svc._section || 'service',
                amount: (Number(svc.price) || 0) * (Number(svc.quantity) || 1),
                booked: !!svc.done,
                prepayment_received: !!svc.prepaymentReceived,
                paid: !!svc.paid,
                day: svc.day,
                start_time: svc.startTime,
                end_time: svc.endTime,
                tour_name: tourName,
                pax: this.state.paxCount,
                tour_start: this.state.tourStart,
                tour_end: this.state.tourEnd
            }));

            const overlay = document.createElement('div');
            overlay.setAttribute('data-modal-overlay', '');
            overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:10000;display:flex;align-items:center;justify-content:center;';

            const buildText = (mode) => {
                const greet = _t('app.checklist.greet', 'Hello, {supplier}!').replace('{supplier}', supplierName);
                const lines = [greet, ''];
                const dateRange = this.state.tourStart
                    ? (this.state.tourEnd ? `${this.state.tourStart} → ${this.state.tourEnd}` : this.state.tourStart)
                    : _t('app.checklist.date_tbd', 'date TBD');
                const tourLabel = _t('app.checklist.tour_label', 'Tour: {name}').replace('{name}', tourName);
                const datesLabel = _t('app.checklist.dates_label', 'Dates: {range}').replace('{range}', dateRange);
                const paxCount = this.state.paxCount || '?';
                if (mode === 'availability') {
                    lines.push(_t('app.checklist.availability_intro', 'Please confirm availability for the following services:'));
                    lines.push(tourLabel, datesLabel, _t('app.checklist.pax_label', 'Number of guests: {pax}').replace('{pax}', paxCount), '');
                    const dayPrefix = _t('app.services_list.day_prefix', 'Day').toLowerCase();
                    items.forEach(s => {
                        const day = s.day ? ` (${dayPrefix} ${s.day}` : '';
                        const time = s.start_time ? ` ${s.start_time}${s.end_time ? '-' + s.end_time : ''}` : '';
                        lines.push(`• ${s.service_name}${day}${time})`);
                    });
                    lines.push('',
                        _t('app.checklist.availability_outro', 'Please confirm availability and pricing.'),
                        _t('app.checklist.thanks', 'Thank you!'));
                } else if (mode === 'booking') {
                    lines.push(_t('app.checklist.booking_intro', 'Please book the following services:'));
                    lines.push(tourLabel, datesLabel, _t('app.checklist.pax_label_short', 'Guests: {pax}').replace('{pax}', paxCount), '');
                    items.forEach(s => {
                        lines.push(`• ${s.service_name}`,
                            _t('app.checklist.booking_day_time', '  Day {day}, {start}-{end}')
                                .replace('{day}', s.day || '—')
                                .replace('{start}', s.start_time || '—')
                                .replace('{end}', s.end_time || '—'),
                            _t('app.checklist.booking_amount', '  Amount: ${amount}').replace('{amount}', Math.round(s.amount)));
                    });
                    lines.push('',
                        _t('app.checklist.booking_pii_note', '— Guest contacts and passport details will be provided separately after confirmation. —'),
                        '', _t('app.checklist.client_label', 'Client: {name}').replace('{name}', this.state.clientName || '?'),
                        this.state.clientPhone ? _t('app.checklist.phone_label', 'Phone: {phone}').replace('{phone}', this.state.clientPhone) : '',
                        this.state.clientEmail ? _t('app.checklist.email_label', 'Email: {email}').replace('{email}', this.state.clientEmail) : '',
                        '', _t('app.checklist.waiting_confirmation', 'Awaiting your confirmation.'));
                } else if (mode === 'reconcile') {
                    const dateStr = new Date().toLocaleDateString((window.i18n?.currentLocale === 'es' ? 'es-AR' : window.i18n?.currentLocale === 'en' ? 'en-US' : 'ru-RU'));
                    lines.push(_t('app.checklist.reconcile_intro', 'Service reconciliation for {tour} ({date}):')
                        .replace('{tour}', tourName).replace('{date}', dateStr));
                    items.forEach(s => {
                        const flags = [];
                        if (s.booked) flags.push(_t('app.checklist.reconcile_status_booked', 'booked'));
                        if (s.prepayment_received) flags.push(_t('app.checklist.reconcile_status_prepayment', 'deposit received'));
                        if (s.paid) flags.push(_t('app.checklist.reconcile_status_paid', 'paid in full'));
                        lines.push(`• ${s.service_name} — ${flags.length ? flags.join(', ') : _t('app.checklist.reconcile_status_none', 'no status')}`);
                    });
                    lines.push('', _t('app.checklist.reconcile_outro', 'Please confirm on your end.'));
                }
                return lines.filter(l => l !== null).join('\n');
            };

            const requestTitle = _t('app.checklist.request_title', 'Request: {supplier}').replace('{supplier}', this.escapeHtml(supplierName));
            overlay.innerHTML = `
                <div style="background:white;border-radius:12px;width:92%;max-width:680px;max-height:90vh;overflow:hidden;display:flex;flex-direction:column;">
                    <div style="padding:14px 20px;border-bottom:1px solid #e5e7eb;display:flex;align-items:center;justify-content:space-between;">
                        <h3 style="margin:0;">${requestTitle}</h3>
                        <button class="btn-icon" id="csr-close" title="${_t('app.checklist.request_close', 'Close (ESC)')}">×</button>
                    </div>
                    <div style="padding:14px 20px;border-bottom:1px solid #e5e7eb;display:flex;gap:12px;flex-wrap:wrap;">
                        <label style="font-size:13px;cursor:pointer;"><input type="radio" name="csr-mode" value="availability" checked> ${_t('app.checklist.request_mode_availability', 'Availability')}</label>
                        <label style="font-size:13px;cursor:pointer;"><input type="radio" name="csr-mode" value="booking"> ${_t('app.checklist.request_mode_booking', 'Booking')}</label>
                        <label style="font-size:13px;cursor:pointer;"><input type="radio" name="csr-mode" value="reconcile"> ${_t('app.checklist.request_mode_reconcile', 'Reconciliation')}</label>
                    </div>
                    <div style="padding:14px 20px;flex:1;overflow:auto;">
                        <textarea id="csr-text" style="width:100%;height:280px;padding:10px;font-family:monospace;font-size:12px;border:1px solid #e5e7eb;border-radius:6px;"></textarea>
                    </div>
                    <div style="padding:12px 20px;border-top:1px solid #e5e7eb;background:#f9fafb;display:flex;gap:8px;justify-content:flex-end;">
                        <button class="btn" id="csr-copy">${_t('app.checklist.request_copy', '📋 Copy')}</button>
                        <button class="btn-primary" id="csr-mailto">${_t('app.checklist.request_open_email', '✉ Open in email')}</button>
                    </div>
                </div>
            `;
            document.body.appendChild(overlay);
            const ta = overlay.querySelector('#csr-text');
            const update = () => {
                const mode = overlay.querySelector('input[name="csr-mode"]:checked').value;
                ta.value = buildText(mode);
            };
            update();
            overlay.querySelectorAll('input[name="csr-mode"]').forEach(r => r.addEventListener('change', update));
            const close = () => overlay.remove();
            overlay.querySelector('#csr-close').addEventListener('click', close);
            overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
            overlay.querySelector('#csr-copy').addEventListener('click', () => {
                ta.select(); navigator.clipboard?.writeText(ta.value);
                const btn = overlay.querySelector('#csr-copy');
                btn.textContent = _t('app.checklist.request_copied', '✓ Copied');
                setTimeout(() => btn.textContent = _t('app.checklist.request_copy', '📋 Copy'), 2000);
            });
            overlay.querySelector('#csr-mailto').addEventListener('click', () => {
                const subj = _t('app.checklist.request_title', 'Request: {supplier}').replace('{supplier}', tourName);
                window.location.href = `mailto:?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(ta.value)}`;
            });
        },

        groupBySupplier(services) {
            const groups = {};
            services.forEach(service => {
                const supplier = service.contractor || '—';
                if (!groups[supplier]) groups[supplier] = [];
                groups[supplier].push(service);
            });

            return Object.keys(groups)
                .sort()
                .map(supplier => ({
                    supplier,
                    services: groups[supplier],
                    total: groups[supplier].reduce((sum, s) => sum + (s.price * s.quantity), 0)
                }));
        },

        // ✅ FIX: Helper для нахождения оригинального service в state
        findServiceInState(serviceId, section) {
            let array;
            switch (section) {
                case 'service':
                    array = this.state.services;
                    break;
                case 'hotel':
                    array = this.state.hotels;
                    break;
                case 'flight':
                    array = this.state.flights;
                    break;
                case 'other':
                    array = this.state.otherServices;
                    break;
                default:
                    return null;
            }
            return array.find(s => s.id === serviceId);
        },

        passesChecklistFilter(service) {
            const filter = this.state.checklistFilter;

            if (filter === 'all') return true;
            if (filter === 'booked') return service.done === true;
            if (filter === 'paid') return service.paid === true;
            if (filter === 'unprocessed') return !service.done && !service.paid;

            return true;
        },

        updateChecklistCounters() {
            const services = this.state.services;

            const booked = services.filter(s => s.done).length;
            const paid = services.filter(s => s.paid).length;
            const unprocessed = services.filter(s => !s.done && !s.paid).length;

            const bookedEl = document.getElementById('cnt-booked');
            const paidEl = document.getElementById('cnt-paid');
            const unprocessedEl = document.getElementById('cnt-unprocessed');

            if (bookedEl) bookedEl.textContent = booked;
            if (paidEl) paidEl.textContent = paid;
            if (unprocessedEl) unprocessedEl.textContent = unprocessed;
        },

        // ===== CHECKLIST RENDERING - Отображение чеклиста бронирований =====

        renderChecklist() {
            const container = document.getElementById('checklist-content');
            if (!container) return;

            container.innerHTML = '';

            // Объединяем все услуги из всех секций
            const allServices = [
                ...this.state.services.map(s => ({ ...s, _section: 'service' })),
                ...this.state.hotels.map(s => ({ ...s, _section: 'hotel' })),
                ...this.state.flights.map(s => ({ ...s, _section: 'flight' })),
                ...this.state.otherServices.map(s => ({ ...s, _section: 'other' }))
            ];

            // Фильтруем услуги
            const filteredServices = allServices.filter(s => this.passesChecklistFilter(s));

            if (filteredServices.length === 0) {
                container.innerHTML = `<div style="text-align: center; padding: 40px; color: var(--gray-500); font-size: 13px;">${_t('app.checklist.empty_state', 'No services to display')}</div>`;
                this.updateChecklistCounters();
                return;
            }

            // Группируем по типам (основные услуги, отели, перелёты, прочие)
            const serviceTypes = [
                { key: 'service', label: _t('app.checklist.type_services', '🎯 Main services'), icon: '🎯' },
                { key: 'hotel', label: _t('app.checklist.type_hotels', '🏨 Hotels'), icon: '🏨' },
                { key: 'flight', label: _t('app.checklist.type_flights', '✈️ Flights'), icon: '✈️' },
                { key: 'other', label: _t('app.checklist.type_other', '📋 Other services'), icon: '📋' }
            ];

            serviceTypes.forEach(typeInfo => {
                const servicesOfType = filteredServices.filter(s => s._section === typeInfo.key);
                if (servicesOfType.length === 0) return;

                // Создаём разделитель типа услуг
                const typeSeparator = document.createElement('div');
                typeSeparator.style.cssText = 'margin: 24px 0 16px 0; padding: 12px; background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-left: 4px solid var(--primary); border-radius: 8px;';
                typeSeparator.innerHTML = `
                    <div style="display: flex; align-items: center; justify-content: space-between;">
                        <div style="font-size: 15px; font-weight: 600; color: var(--gray-900);">${typeInfo.label}</div>
                        <div style="font-size: 12px; color: var(--gray-600);">${servicesOfType.length} ${servicesOfType.length === 1 ? _t('app.checklist.count_one', 'service') : _t('app.checklist.count_many', 'services')}</div>
                    </div>
                `;
                container.appendChild(typeSeparator);

                // Группируем услуги этого типа по дням или поставщикам
                const groups = this.state.checklistViewMode === 'day'
                    ? this.groupByDay(servicesOfType)
                    : this.groupBySupplier(servicesOfType);

                // Рендерим группы
                groups.forEach(group => {
                    const groupDiv = this.renderChecklistGroup(group);
                    container.appendChild(groupDiv);
                });
            });

            // Обновляем счетчики
            this.updateChecklistCounters();

            // Обновляем иконки Lucide
            if (typeof lucide !== 'undefined') {
                lucide.createIcons();
            }
        },

        renderChecklistGroup(group) {
            const div = document.createElement('div');
            div.className = 'checklist-group';
            div.style.cssText = 'background: white; border-radius: 8px; padding: 16px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); margin-bottom: 16px;';

            // Header
            const header = document.createElement('div');
            header.style.cssText = 'display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid var(--gray-200);';

            const title = document.createElement('div');
            title.style.cssText = 'font-weight: 600; font-size: 14px; color: var(--gray-900);';
            title.textContent = this.state.checklistViewMode === 'day'
                ? _t('app.checklist.day_label', 'Day {day}').replace('{day}', group.day)
                : _t('app.checklist.supplier_label', 'Supplier: {name}').replace('{name}', group.supplier);

            const total = document.createElement('div');
            total.style.cssText = 'font-size: 12px; color: var(--gray-600); margin-left: auto; margin-right: 12px;';
            total.textContent = _t('app.checklist.total_label', 'Total: {amount}')
                .replace('{amount}', `${this.config.currency}${group.total.toFixed(2)}`);

            header.appendChild(title);
            header.appendChild(total);

            // Sprint 19h: для группировки "По поставщикам" — кнопка "Запрос поставщику"
            const noSupplierValue = _t('app.checklist.no_supplier_value', 'No supplier');
            if (this.state.checklistViewMode === 'supplier' && group.supplier && group.supplier !== noSupplierValue) {
                const reqBtn = document.createElement('button');
                reqBtn.className = 'btn btn-sm';
                reqBtn.style.cssText = 'padding: 4px 10px; font-size: 11px; background: #2563eb; color: white;';
                reqBtn.innerHTML = `<i data-lucide="send" style="width:11px;height:11px;vertical-align:-1px"></i> ${_t('app.checklist.request_btn_send', 'Request')}`;
                reqBtn.title = _t('app.checklist.request_btn_title', 'Compose supplier request for {supplier}').replace('{supplier}', group.supplier);
                reqBtn.onclick = (e) => {
                    e.stopPropagation();
                    this.openChecklistSupplierRequest(group);
                };
                header.appendChild(reqBtn);
            }

            div.appendChild(header);

            // Services
            group.services.forEach(service => {
                const card = this.renderChecklistServiceCard(service);
                div.appendChild(card);
            });

            return div;
        },

        renderChecklistServiceCard(service) {
            const card = document.createElement('div');
            card.className = 'checklist-service-card';
            card.style.cssText = `
                border: 1px solid var(--gray-200);
                border-radius: 6px;
                padding: 12px;
                margin-bottom: 8px;
                background: ${service.done ? '#ecfdf5' : 'white'};
                border-left: 4px solid ${service.paid ? '#10b981' : service.done ? '#f59e0b' : '#e5e7eb'};
            `;

            // Main row: checkboxes + info + price badge
            const mainRow = document.createElement('div');
            mainRow.style.cssText = 'display: flex; align-items: flex-start; gap: 12px; margin-bottom: 8px;';

            // Checkbox "Бронь"
            const doneWrap = document.createElement('div');
            doneWrap.style.cssText = 'display: flex; flex-direction: column; align-items: center;';

            const doneCheckbox = document.createElement('input');
            doneCheckbox.type = 'checkbox';
            doneCheckbox.checked = service.done;
            doneCheckbox.style.cssText = 'width: 16px; height: 16px; cursor: pointer;';
            doneCheckbox.addEventListener('change', () => {
                // ✅ FIX: Находим оригинальный service в state по ID и секции
                const originalService = this.findServiceInState(service.id, service._section);
                if (originalService) {
                    originalService.done = doneCheckbox.checked;
                    this.saveToLocalStorage();
                    // Обновить стили карточки без полной перерисовки
                    card.style.background = originalService.done ? '#ecfdf5' : 'white';
                    card.style.borderLeft = `4px solid ${originalService.paid ? '#10b981' : originalService.done ? '#f59e0b' : '#e5e7eb'}`;
                    // Синхронизировать с конструктором смет
                    this.renderServices();
                    this.renderHotels();
                    this.renderFlights();
                    this.renderOtherServices();
                }
            });

            const doneLabel = document.createElement('div');
            doneLabel.style.cssText = 'font-size: 10px; color: var(--gray-600); margin-top: 2px;';
            doneLabel.textContent = _t('app.checklist.checkbox_booking', 'Booked');

            doneWrap.appendChild(doneCheckbox);
            doneWrap.appendChild(doneLabel);

            // Checkbox "Оплата"
            const paidWrap = document.createElement('div');
            paidWrap.style.cssText = 'display: flex; flex-direction: column; align-items: center;';

            const paidCheckbox = document.createElement('input');
            paidCheckbox.type = 'checkbox';
            paidCheckbox.checked = service.paid;
            paidCheckbox.style.cssText = 'width: 16px; height: 16px; cursor: pointer;';
            paidCheckbox.addEventListener('change', () => {
                // ✅ FIX: Находим оригинальный service в state по ID и секции
                const originalService = this.findServiceInState(service.id, service._section);
                if (originalService) {
                    originalService.paid = paidCheckbox.checked;
                    this.saveToLocalStorage();
                    // Обновить стили карточки без полной перерисовки
                    card.style.background = originalService.done ? '#ecfdf5' : 'white';
                    card.style.borderLeft = `4px solid ${originalService.paid ? '#10b981' : originalService.done ? '#f59e0b' : '#e5e7eb'}`;
                    // Синхронизировать с конструктором смет
                    this.renderServices();
                    this.renderHotels();
                    this.renderFlights();
                    this.renderOtherServices();
                }
            });

            const paidLabel = document.createElement('div');
            paidLabel.style.cssText = 'font-size: 10px; color: var(--gray-600); margin-top: 2px;';
            paidLabel.textContent = _t('app.checklist.checkbox_payment', 'Paid');

            paidWrap.appendChild(paidCheckbox);
            paidWrap.appendChild(paidLabel);

            // Checkbox "Предоплата"
            const prepaymentWrap = document.createElement('div');
            prepaymentWrap.style.cssText = 'display: flex; flex-direction: column; align-items: center;';

            const prepaymentCheckbox = document.createElement('input');
            prepaymentCheckbox.type = 'checkbox';
            prepaymentCheckbox.checked = service.prepaymentReceived || false;
            prepaymentCheckbox.style.cssText = 'width: 16px; height: 16px; cursor: pointer;';
            prepaymentCheckbox.addEventListener('change', () => {
                // ✅ FIX: Находим оригинальный service в state по ID и секции
                const originalService = this.findServiceInState(service.id, service._section);
                if (originalService) {
                    originalService.prepaymentReceived = prepaymentCheckbox.checked;
                    this.saveToLocalStorage();
                    // Обновить стили карточки без полной перерисовки
                    card.style.background = originalService.done ? '#ecfdf5' : 'white';
                    card.style.borderLeft = `4px solid ${originalService.paid ? '#10b981' : originalService.done ? '#f59e0b' : '#e5e7eb'}`;
                    // Синхронизировать с конструктором смет
                    this.renderServices();
                    this.renderHotels();
                    this.renderFlights();
                    this.renderOtherServices();
                }
            });

            const prepaymentLabel = document.createElement('div');
            prepaymentLabel.style.cssText = 'font-size: 10px; color: var(--gray-600); margin-top: 2px;';
            prepaymentLabel.textContent = _t('app.checklist.checkbox_prepayment', 'Deposit');

            prepaymentWrap.appendChild(prepaymentCheckbox);
            prepaymentWrap.appendChild(prepaymentLabel);

            // Info
            const info = document.createElement('div');
            info.style.cssText = 'flex: 1;';

            const serviceName = document.createElement('div');
            serviceName.style.cssText = 'font-weight: 500; font-size: 12px; margin-bottom: 4px;';
            serviceName.textContent = `${service.quantity} × ${service.name}`;

            const serviceDetails = document.createElement('div');
            serviceDetails.style.cssText = 'font-size: 11px; color: var(--gray-600);';

            // Показываем предоплату если указана
            let detailsText = `${_t('app.services_list.day_prefix', 'Day')} ${service.day} • ${service.contractor || '—'} • ${this.config.currency}${service.price}`;
            if (service.prepayment && service.prepayment > 0) {
                detailsText += ` • ${_t('app.checklist.prepayment_amount', '💳 Deposit: {amount}').replace('{amount}', `${this.config.currency}${service.prepayment}`)}`;
            }
            serviceDetails.textContent = detailsText;

            info.appendChild(serviceName);
            info.appendChild(serviceDetails);

            // Price badge - показываем остаток к оплате с учётом предоплаты
            const totalPrice = service.price * service.quantity;
            const prepaymentAmount = (service.prepayment || 0) * (service.prepaymentReceived ? 1 : 0);
            const remainingAmount = totalPrice - prepaymentAmount;

            const priceBadge = document.createElement('div');
            priceBadge.style.cssText = `
                background: ${service.paid ? '#10b981' : '#e5e7eb'};
                color: ${service.paid ? 'white' : '#374151'};
                padding: 4px 8px;
                border-radius: 6px;
                font-size: 11px;
                font-weight: 600;
                display: flex;
                flex-direction: column;
                align-items: flex-end;
            `;

            // Показываем остаток если была предоплата
            if (prepaymentAmount > 0) {
                priceBadge.innerHTML = `
                    <div style="font-size: 9px; opacity: 0.7;">${_t('app.checklist.remaining_to_pay', 'Balance due:')}</div>
                    <div>${this.config.currency}${remainingAmount.toFixed(2)}</div>
                `;
            } else {
                priceBadge.textContent = `${this.config.currency}${totalPrice.toFixed(2)}`;
            }

            mainRow.appendChild(doneWrap);
            mainRow.appendChild(paidWrap);
            mainRow.appendChild(prepaymentWrap);
            mainRow.appendChild(info);
            mainRow.appendChild(priceBadge);

            card.appendChild(mainRow);

            // Toggle button for collapsible details
            const toggleBtn = document.createElement('button');
            toggleBtn.style.cssText = 'width: 100%; padding: 6px; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 10px; color: #6b7280; cursor: pointer; display: flex; align-items: center; gap: 6px; margin-bottom: 8px; transition: background 0.2s;';
            const detailsToggleLabel = _t('app.checklist.details_toggle', 'Details (deposit, timing, notes)');
            toggleBtn.innerHTML = `<span class="toggle-icon" style="font-size: 8px;">▶</span><span>${detailsToggleLabel}</span>`;

            const hasDetails = service.prepayment || service.prepaymentDeadline || service.startTime || service.endTime || service.comment;
            if (hasDetails) {
                toggleBtn.innerHTML = `<span class="toggle-icon" style="font-size: 8px;">▶</span><span>${detailsToggleLabel}</span><span style="font-size: 8px; color: #3b82f6; margin-left: auto;">●</span>`;
            }

            const detailsContent = document.createElement('div');
            detailsContent.className = 'checklist-details-content';
            detailsContent.style.cssText = 'max-height: 0; overflow: hidden; transition: max-height 0.3s ease;';

            // Останавливаем всплытие ВСЕХ событий из detailsContent чтобы не срабатывал toggle
            detailsContent.addEventListener('click', (e) => e.stopPropagation());
            detailsContent.addEventListener('mousedown', (e) => e.stopPropagation());
            detailsContent.addEventListener('mouseup', (e) => e.stopPropagation());
            detailsContent.addEventListener('keydown', (e) => e.stopPropagation());
            detailsContent.addEventListener('keyup', (e) => e.stopPropagation());
            detailsContent.addEventListener('input', (e) => e.stopPropagation());
            detailsContent.addEventListener('change', (e) => e.stopPropagation());

            toggleBtn.addEventListener('click', (e) => {
                e.stopPropagation(); // Останавливаем всплытие от кнопки тоже
                const isExpanded = detailsContent.style.maxHeight && detailsContent.style.maxHeight !== '0px';
                if (isExpanded) {
                    detailsContent.style.maxHeight = '0';
                    toggleBtn.querySelector('.toggle-icon').textContent = '▶';
                    // Удаляем из Set раскрытых секций
                    this.expandedChecklistDetails.delete(service.id);
                } else {
                    // Используем большое фиксированное значение вместо scrollHeight
                    // чтобы избежать проблем с изменяющимся контентом
                    detailsContent.style.maxHeight = '500px';
                    toggleBtn.querySelector('.toggle-icon').textContent = '▼';
                    // Добавляем в Set раскрытых секций
                    this.expandedChecklistDetails.add(service.id);
                }
            });

            // Автоматически раскрываем секцию если она была раскрыта до перерисовки
            if (this.expandedChecklistDetails.has(service.id)) {
                detailsContent.style.maxHeight = '500px';
                toggleBtn.querySelector('.toggle-icon').textContent = '▼';
            }

            card.appendChild(toggleBtn);

            // Prepayment info
            if (service.prepayment && service.prepayment > 0) {
                const prepaymentInfo = document.createElement('div');
                prepaymentInfo.style.cssText = 'padding: 8px; background: #fef3c7; border-radius: 4px; margin-bottom: 8px; font-size: 11px;';
                const depositLine = _t('app.checklist.prepayment_amount', '💳 Deposit: {amount}')
                    .replace('{amount}', `${this.config.currency}${service.prepayment}`);
                const dueLine = service.prepaymentDeadline
                    ? _t('app.checklist.prepayment_deadline_label', '📅 Due: {date}').replace('{date}', service.prepaymentDeadline)
                    : '';
                prepaymentInfo.innerHTML = `
                    <div style="color: #92400e; font-weight: 500; margin-bottom: 4px;">${depositLine}</div>
                    ${dueLine ? `<div style="color: #78350f;">${dueLine}</div>` : ''}
                `;
                detailsContent.appendChild(prepaymentInfo);
            }

            // Timing row (время начала/окончания)
            const timingRow = document.createElement('div');
            timingRow.style.cssText = 'display: flex; gap: 12px; margin-bottom: 8px; padding: 8px; background: #f9fafb; border-radius: 4px;';

            const startTimeWrap = document.createElement('div');
            startTimeWrap.style.cssText = 'flex: 1;';
            const startTimeLabel = document.createElement('label');
            startTimeLabel.style.cssText = 'font-size: 10px; color: var(--gray-600); display: block; margin-bottom: 2px;';
            startTimeLabel.textContent = _t('app.checklist.time_start', '⏰ Start time');
            const startTimeInput = document.createElement('input');
            startTimeInput.type = 'time';
            startTimeInput.value = service.startTime || '';
            startTimeInput.style.cssText = 'width: 100%; padding: 4px; border: 1px solid var(--gray-300); border-radius: 4px; font-size: 11px;';
            startTimeInput.addEventListener('change', () => {
                service.startTime = startTimeInput.value;
                this.saveToLocalStorage();
                this.markQuoteAsUnsaved();
            });
            startTimeWrap.appendChild(startTimeLabel);
            startTimeWrap.appendChild(startTimeInput);

            const endTimeWrap = document.createElement('div');
            endTimeWrap.style.cssText = 'flex: 1;';
            const endTimeLabel = document.createElement('label');
            endTimeLabel.style.cssText = 'font-size: 10px; color: var(--gray-600); display: block; margin-bottom: 2px;';
            endTimeLabel.textContent = _t('app.checklist.time_end', '⏰ End time');
            const endTimeInput = document.createElement('input');
            endTimeInput.type = 'time';
            endTimeInput.value = service.endTime || '';
            endTimeInput.style.cssText = 'width: 100%; padding: 4px; border: 1px solid var(--gray-300); border-radius: 4px; font-size: 11px;';
            endTimeInput.addEventListener('change', () => {
                service.endTime = endTimeInput.value;
                this.saveToLocalStorage();
                this.markQuoteAsUnsaved();
            });
            endTimeWrap.appendChild(endTimeLabel);
            endTimeWrap.appendChild(endTimeInput);

            timingRow.appendChild(startTimeWrap);
            timingRow.appendChild(endTimeWrap);

            detailsContent.appendChild(timingRow);

            // Comment textarea
            const commentTextarea = document.createElement('textarea');
            commentTextarea.placeholder = _t('app.checklist.comment_placeholder', 'Note...');
            commentTextarea.value = service.comment || '';
            commentTextarea.style.cssText = 'width: 100%; min-height: 50px; padding: 6px; border: 1px solid var(--gray-300); border-radius: 4px; font-size: 11px; resize: vertical;';
            commentTextarea.addEventListener('input', () => {
                service.comment = commentTextarea.value;
                this.saveToLocalStorage();
            });

            detailsContent.appendChild(commentTextarea);

            card.appendChild(detailsContent);

            return card;
        },
    });
}
